import './screens.css';
import { el, setText, text } from './walkout.js';
import { needs } from './contract.js';

/**
 * The front door.
 *
 * Until step 66 the game had none: it booted, loaded whatever was in
 * `localStorage`, and put you on the marks. That works exactly as long as there
 * is only ever one career and you never want to start another — and there has
 * been a way to throw one away since step 53, buried in the debug panel behind
 * two clicks.
 *
 * Two doors, and the interesting one is **continue**. It reads the save without
 * loading it (`store.describe()`), which matters: a payload this version cannot
 * read must be refused *whole* (gotcha 61), and a title screen that
 * half-applied one in order to print a name on a button would be the exact bug
 * `restoreCareer` exists to prevent. So the button either says who you are and
 * where you got to, or it is not offered and the screen says why in a sentence.
 *
 * It reads and decides nothing. `main.js` owns what "continue" and "new" mean.
 */
export function createTitle({ store, onContinue, onNew, container = document.body }) {
  needs(store, ['describe'], { who: 'the title screen', what: 'the save store' });
  const root = el('div', 'screen screen--title');
  root.hidden = true;

  const mark = el('div', 'title__mark');
  mark.append(text('div', 'title__name', 'FIGHTGAME'), text('div', 'title__sub', 'the last train'));
  root.append(mark);

  const doors = el('div', 'title__doors');

  const resume = el('button', 'ui-button title__door');
  resume.dataset.tone = 'go';
  resume.textContent = 'continue';
  const resumeMeta = text('div', 'title__meta', '');

  const fresh = el('button', 'ui-button title__door');
  fresh.textContent = 'new career';
  const freshMeta = text('div', 'title__meta', 'name yourself, pick your styles, start at eleven');

  doors.append(resume, resumeMeta, fresh, freshMeta);
  root.append(doors);

  const note = text('div', 'title__note', '');
  root.append(note);
  root.append(text('div', 'screen__hint', 'enter for the top one'));

  container.append(root);

  let up = false;
  /** What the save said last time the screen went up. */
  let saved = null;

  /** How long ago, in the roughest terms that are still true. */
  function when(iso) {
    const at = Date.parse(iso ?? '');
    if (!Number.isFinite(at)) return 'a previous session';
    const seconds = Math.max(0, (Date.now() - at) / 1000);
    if (seconds < 90) return 'just now';
    if (seconds < 3600) return `${Math.round(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)} hours ago`;
    return `${Math.round(seconds / 86400)} days ago`;
  }

  function show() {
    saved = store.describe();
    const has = Boolean(saved?.ok);

    resume.hidden = !has;
    resumeMeta.hidden = !has;
    if (has) {
      const record = `${saved.wins}W ${saved.losses}L${saved.draws ? ` ${saved.draws}D` : ''}`;
      const rank = saved.rank ? `rank ${saved.rank} of ${saved.of}` : 'unranked';
      setText(
        resumeMeta,
        `${saved.name ?? 'your fighter'} · ${rank} · ${record} · saved ${when(saved.at)}`
      );
    }

    // Only worth saying when there *was* something and it could not be used.
    // "no career saved yet" is not a failure, it is a first visit.
    const broken = saved && !saved.ok && saved.reason && !/no career saved/.test(saved.reason);
    note.hidden = !broken;
    if (broken) setText(note, `there is a save here and this version cannot read it — ${saved.reason}`);

    root.hidden = false;
    up = true;
    (has ? resume : fresh).focus?.();
    return true;
  }

  function leave(go) {
    if (!up) return false;
    up = false;
    root.hidden = true;
    go();
    return true;
  }

  const take = () => leave(onContinue);
  const start = () => leave(onNew);

  function onKey(event) {
    if (!up) return;
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (saved?.ok) take();
    else start();
  }

  resume.addEventListener('click', take);
  fresh.addEventListener('click', start);
  window.addEventListener('keydown', onKey);

  return {
    element: root,
    get up() {
      return up;
    },
    show,
    /** For a headless run, which has nobody to press anything. */
    take,
    start,
    dispose() {
      window.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}
